import PageContainer from "../../components/PageContainer/PageContainer";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Header from "../../components/atoms/Header";
import React, { Fragment } from "react";
import {
  useGetSafetyCheckByTypeQuery,
  useGetVehicleByIdQuery,
  useSubmitChecklistMutation,
} from "../../redux/checklistStoreAPI";
import {
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { COLORS } from "../../assets/theme";
import useButtonStyles from "../../hooks/styles/useButtonStyles";
import { Formik, Form, FormikHelpers } from "formik";
import {
  ASafetyCheckWithAnswer,
  ASafetyCheckWithAnswerFormik,
} from "../../types/safetycheck";
import { toast } from "react-toastify";
import { ROUTES } from "../../config/nav";

const useStyles = makeStyles((_) => ({
  text: {
    fontFamily: "'Raleway', sans-serif",
  },
  headerText: {
    fontWeight: "bold",
    color: COLORS.text_black,
    fontSize: 12,
    textTransform: "uppercase",
  },
  labelText: {
    fontWeight: "bold",
    color: COLORS.text_black,
    fontSize: 12,

    padding: "9px 8px",
  },
  radio: {
    // color:
    color: COLORS.azure,
    "&$checked": {
      color: COLORS.azure,
    },
  },

  radioLabel: {
    "&>span": {
      fontFamily: "'Raleway', sans-serif",
      fontWeight: "bold",
    },
  },

  withPadding: {
    padding: "9px 8px",
  },

  detail: {
    paddingTop: 0,
    paddingLeft: 8,
    paddingRight: 8,
    paddingBottom: 0,
  },

  bottom_nav: {
    width: "100%",
    maxWidth: "500px",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    padding: "30px 24px",
    // backgroundColor: "red",
  },
}));

const SafetyChecklistPage = () => {
  const { id: vehicleId } = useParams<"id">();
  const classes = useStyles();
  const btnClasses = useButtonStyles();
  const { currentData: d } = useGetVehicleByIdQuery(vehicleId ?? "", {
    skip: !vehicleId,
  });
  const [submitCheck, { isLoading: isLoadingSubmitCheck }] =
    useSubmitChecklistMutation();
  const [isSubmittingCheck, setIsSubmittingCheck] =
    React.useState<boolean>(false);
  const isLoadingSubmit = isSubmittingCheck || isLoadingSubmitCheck;
  const {
    currentData: s,
    isLoading: isLoadingSafetyCheckQuestions,
    isFetching: isFetchingSafetyCheckQuestions,
  } = useGetSafetyCheckByTypeQuery(d?.type ?? "", {
    skip: !d || !d.type,
  });

  const divRef = React.useRef<HTMLDivElement[]>([]);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const page = params.get("p") ?? "1";
  const activePage = parseInt(page);
  const loading =
    isLoadingSafetyCheckQuestions || isFetchingSafetyCheckQuestions;

  React.useEffect(() => {
    if (!params.get("p")) {
      params.set("p", "1");
      setParams(params, { replace: true });
    }
  }, [params]);

  const [initialValues, setInitialValues] =
    React.useState<ASafetyCheckWithAnswerFormik>({ data: [] });

  React.useEffect(() => {
    if (!isLoadingSafetyCheckQuestions && s) {
      let safetyCheckDataAnswers: ASafetyCheckWithAnswer[] = [];
      for (let index = 0; index < s.length; index++) {
        const list = s[index].list;
        for (let idxlist = 0; idxlist < list.length; idxlist++) {
          const data = list[idxlist];
          safetyCheckDataAnswers.push({ ...data, answer: null });
        }
      }
      setInitialValues({ data: safetyCheckDataAnswers });
    }
  }, [isLoadingSafetyCheckQuestions]);

  React.useEffect(() => {
    divRef.current = divRef.current.slice(0, s?.length);
  }, [s?.length]);

  const onSubmit = React.useCallback(
    (values: typeof initialValues, _?: FormikHelpers<typeof initialValues>) => {
      if (!vehicleId) return;
      setIsSubmittingCheck(true);
      submitCheck({ vehicleId, data: values.data })
        .unwrap()
        .then(({ data: { id } }) => {
          setIsSubmittingCheck(false);

          navigate({
            pathname: `${ROUTES.admin}/result/${id}`,
          });
          window.scrollTo(0, 0);
        })
        .catch(() => {
          setIsSubmittingCheck(false);
          toast("Gagal submit checklist. Silahkan periksa kembali data anda.", {
            type: "error",
          });
        });
    },
    [vehicleId]
  );

  return (
    <PageContainer
      headerNavigationContent={
        <Header
          backButtonOnClick={() => {
            navigate(-1);
          }}
          showBackButton={true}
          title={
            s && s.length > 0
              ? `Data Cek Kendaraan ${activePage}/${s.length}`
              : "Data Cek Kendaraan"
          }
        />
      }
      withBackground
      mainContainerStyle={{
        padding: "16px 0 72px 0",
        minHeight: "100vh",
      }}
    >
      <Fragment>
        <Formik
          enableReinitialize
          onSubmit={onSubmit}
          initialValues={initialValues}
        >
          {({ handleChange, values }) => {
            const activeCategory =
              (s && s.find((_, index) => index === activePage - 1)?.title) ??
              "";
            const existValues = values.data.filter(
              (value) => value.category === activeCategory
            );
            const isSomeValuesByCategoryAreNull = existValues.some(
              (value) => value.answer === null
            );
            return (
              <Form id="safetyChecklistForm">
                {loading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: 8,
                    }}
                  >
                    <CircularProgress
                      size={24}
                      color="inherit"
                      style={{ color: COLORS.blue_1 }}
                    />
                  </div>
                ) : s && s.length > 0 ? (
                  <>
                    {s.map(({ title, list }, idx) => {
                      return (
                        <div
                          ref={(ref) => divRef.current[idx] === ref}
                          key={title}
                          style={{
                            display:
                              activePage === idx + 1 ? "contents" : "none",
                          }}
                        >
                          <Typography
                            className={[
                              classes.text,
                              classes.headerText,
                              classes.withPadding,
                            ].join(" ")}
                          >
                            {idx + 1}. {title}
                          </Typography>
                          <div
                            className={classes.withPadding}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            {list.map(({ category, seq, question }, index) => {
                              return (
                                <React.Fragment
                                  key={`${category}-${seq}-${index}`}
                                >
                                  <FormControl>
                                    <FormLabel
                                      className={[
                                        classes.text,
                                        classes.labelText,
                                      ].join(" ")}
                                    >
                                      {question}?
                                    </FormLabel>
                                    <RadioGroup
                                      row
                                      name={`data[${seq - 1}].answer`}
                                      onChange={handleChange}
                                      style={{
                                        justifyContent: "space-around",
                                      }}
                                    >
                                      <FormControlLabel
                                        value="y"
                                        className={[
                                          classes.text,
                                          classes.radioLabel,
                                        ].join(" ")}
                                        control={
                                          <Radio
                                            color="default"
                                            className={classes.radio}
                                          />
                                        }
                                        label="YA"
                                      />
                                      <FormControlLabel
                                        value="n"
                                        className={[
                                          classes.text,
                                          classes.radioLabel,
                                        ].join(" ")}
                                        control={
                                          <Radio
                                            color="default"
                                            className={classes.radio}
                                          />
                                        }
                                        label="TIDAK"
                                      />
                                    </RadioGroup>
                                  </FormControl>
                                </React.Fragment>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    <div className={classes.withPadding}>
                      {activePage === s?.length ? (
                        <button
                          className={[
                            btnClasses.button,
                            isLoadingSubmit || isSomeValuesByCategoryAreNull
                              ? "disabled"
                              : "secondary",
                            "full-width",
                          ].join(" ")}
                          type="submit"
                          form="safetyChecklistForm"
                          disabled={
                            isLoadingSubmit || isSomeValuesByCategoryAreNull
                          }
                          style={{ height: 48, fontSize: 12 }}
                        >
                          {isLoadingSubmit
                            ? "Loading..."
                            : "SIMPAN SAFETY CHECKLIST"}
                        </button>
                      ) : null}
                    </div>
                    {s && s.length > 0 ? (
                      <div className={[classes.bottom_nav].join(" ")}>
                        <button
                          style={{
                            visibility: activePage <= 1 ? "hidden" : "visible",
                          }}
                          type="button"
                          className={[btnClasses.button, "white"].join(" ")}
                          disabled={activePage <= 1}
                          onClick={() => {
                            params.set("p", `${activePage - 1}`);
                            setParams(params);
                          }}
                        >
                          Sebelumnya
                        </button>
                        <button
                          style={{
                            visibility:
                              activePage === s?.length ? "hidden" : "visible",
                          }}
                          type="button"
                          className={[
                            btnClasses.button,
                            isSomeValuesByCategoryAreNull
                              ? "disabled"
                              : "secondary",
                          ].join(" ")}
                          disabled={
                            activePage === s?.length ||
                            isSomeValuesByCategoryAreNull
                          }
                          onClick={() => {
                            window.scrollTo({ top: 0 });
                            params.set("p", `${activePage + 1}`);
                            setParams(params);
                          }}
                        >
                          Berikutnya
                        </button>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <p style={{ textAlign: "center" }}>
                    Daftar pertanyaan checklist tidak tersedia.
                  </p>
                )}
              </Form>
            );
          }}
        </Formik>
      </Fragment>
    </PageContainer>
  );
};

export default SafetyChecklistPage;
